/**
 * ZoneDrawingMapOptimized Component
 *
 * Optimized version of ZoneDrawingMap with dealer-level bubbles for performance
 * Shows dealer locations with recall counts instead of individual recall markers
 */

import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import type * as GeoJSON from 'geojson';

// Restore original dealer location types
import type { DealerLocation } from '../hooks/useViewportRecallsForZoneMap';
import type { Zone } from '@/services/api/tenants';
import {
  getZoneColor,
  getZoneSizeLabel,
  getZoneBorderColor,
  getZoneDashPattern
} from '../utils/zoneStatistics';

// Helper function to get short size label
const getShortSizeLabel = (recallCount: number): string => {
  if (recallCount < 300) return 'S';
  if (recallCount < 500) return 'M';
  if (recallCount < 700) return 'L';
  return 'XL';
};

const getSizeLabelColor = (recallCount: number): string => {
  // Use CSS variables that match the application theme
  if (recallCount < 300) return 'hsl(var(--primary))'; // primary color
  if (recallCount < 500) return 'hsl(var(--primary))'; // primary color
  if (recallCount < 700) return 'hsl(var(--primary))'; // primary color
  return 'hsl(var(--destructive))'; // destructive color for XL
};

export interface ZoneDrawingMapOptimizedProps {
  dealerLocations: DealerLocation[];
  zones: Zone[];
  onShapeDrawn: (geometry: GeoJSON.Geometry) => void;
  onMapBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  selectedZoneId?: string | null;
  loading?: boolean;
  onZoneDelete?: (zoneId: string) => void;
  onRemoveZipCode?: (zone: Zone, zipCode: string) => void;
  clearDrawnShapes?: boolean;
  showBubbles?: boolean;
  uniformColor?: boolean;
}

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Create custom icon for dealer bubbles (restored original)
const createDealerIcon = (recallCount: number) => {
  let size;
  if (recallCount === 1) {
    size = 25;
  } else if (recallCount <= 5) {
    size = 30;
  } else if (recallCount <= 10) {
    size = 35;
  } else if (recallCount <= 20) {
    size = 40;
  } else {
    size = 45;
  }

  const baseColor = '#7C5F2C'; // Bronze from Vehix (restored original)
  let opacity;
  if (recallCount === 1) {
    opacity = 0.7;
  } else if (recallCount <= 5) {
    opacity = 0.8;
  } else if (recallCount <= 15) {
    opacity = 0.9;
  } else {
    opacity = 1.0;
  }

  const backgroundColor = recallCount > 15 ? '#5C4521' : baseColor;
  const textColor = '#FFFFFF';

  const html = `<div style="background-color: ${backgroundColor}; color: ${textColor}; 
    border-radius: 50%; 
    width: ${size}px; height: ${size}px; 
    display: flex; align-items: center; justify-content: center;
    font-weight: bold; border: 1px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    font-size: 12px; font-family: Arial, sans-serif; opacity: ${opacity};
    transition: all 0.2s ease;">
    ${recallCount}
    </div>`;

  return L.divIcon({
    html: html,
    className: 'custom-marker-icon',
    iconSize: L.point(size, size, true)
  });
};

// Memoized component to handle map initialization and drawing controls

const MapController = React.memo<{
  onShapeDrawn: (geometry: GeoJSON.Geometry) => void;
  dealerLocations: DealerLocation[];
  zones: Zone[];
  selectedZoneId?: string | null;
  onMapBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onZoneDelete?: (zoneId: string) => void;
  onRemoveZipCode?: (zone: Zone, zipCode: string) => void;
  clearDrawnShapes?: boolean;
  showBubbles?: boolean;
  uniformColor?: boolean;
}>(
  ({
    onShapeDrawn,
    dealerLocations,
    zones,
    selectedZoneId,
    onMapBoundsChange,
    onZoneDelete,
    onRemoveZipCode,
    clearDrawnShapes,
    showBubbles = true,
    uniformColor = false
  }) => {
    const map = useMap();
    const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const editableZonesRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const drawControlRef = useRef<L.Control.Draw | null>(null);
    const markerClusterGroupRef = useRef<any>(null);
    const zoneLayersRef = useRef<Map<string, L.GeoJSON>>(new Map());
    const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Dynamically load leaflet-draw CSS to avoid Vite build issues
    useEffect(() => {
      if (typeof document !== 'undefined') {
        // Check if already loaded
        const existingLink = document.querySelector('link[data-leaflet-draw-css]');
        if (!existingLink) {
          // Use CDN fallback for leaflet-draw CSS
          try {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.setAttribute('data-leaflet-draw-css', 'true');
            link.href = 'https://unpkg.com/leaflet-draw@1.0.4/dist/leaflet.draw.css';
            document.head.appendChild(link);
          } catch (error) {}
        }
      }
    }, []);

    // Fix map size calculation - ensures shapes render correctly on all sides
    useEffect(() => {
      // Invalidate size when map is ready to fix rendering issues
      const timer = setTimeout(() => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      }, 100);

      // Second invalidation for tab-switch scenarios where container
      // dimensions may not be final at 100ms (Radix TabsContent mount)
      const timer2 = setTimeout(() => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      }, 300);

      // Also invalidate on window resize
      const handleResize = () => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
        window.removeEventListener('resize', handleResize);
      };
    }, [map]);

    // Initialize drawing controls and add custom popup styles
    useEffect(() => {
      // Add custom CSS for modern popup styling and enhanced zone boundaries
      const styleId = 'modern-zone-popup-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
        /* Clean zone boundary styles */
        .zone-boundary {
          transition: opacity 0.2s ease;
        }
        
        .zone-boundary:hover {
          opacity: 1 !important;
        }
        
        /* Modern popup styles */
        .modern-zone-popup .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          background: transparent;
        }
        .modern-zone-popup .leaflet-popup-content {
          margin: 0;
          line-height: 1.4;
        }
        .modern-zone-popup .leaflet-popup-tip {
          background: hsl(var(--background));
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .modern-zone-popup .leaflet-popup-close-button {
          background: hsl(var(--muted));
          border-radius: 50%;
          width: 24px;
          height: 24px;
          text-align: center;
          line-height: 22px;
          right: 8px;
          top: 8px;
          color: hsl(var(--muted-foreground));
          font-size: 14px;
          font-weight: bold;
          border: 1px solid hsl(var(--border));
          transition: all 0.15s ease;
        }
        .modern-zone-popup .leaflet-popup-close-button:hover {
          background: hsl(var(--destructive));
          color: hsl(var(--destructive-foreground));
          border-color: hsl(var(--destructive));
        }
      `;
        document.head.appendChild(style);
      }

      drawnItemsRef.current.addTo(map);
      editableZonesRef.current.addTo(map);

      drawControlRef.current = new L.Control.Draw({
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: false,
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0, // Full opacity for better visibility
              fillColor: '#d4854a',
              fillOpacity: 0.2
            }
          },
          polyline: {
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0 // Full opacity for better visibility
            }
          },
          rectangle: {
            showArea: false,
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0, // Full opacity for better visibility
              fillColor: '#d4854a',
              fillOpacity: 0.2
            }
          },
          circle: false, // Disabled - not fully tested
          marker: false,
          circlemarker: false
        },
        edit: false as any // Disabled - edit and delete buttons not fully tested
      });

      map.addControl(drawControlRef.current);

      const handleDrawCreated = (e: any) => {
        const layer = e.layer;
        drawnItemsRef.current.clearLayers();
        drawnItemsRef.current.addLayer(layer);
        const geojson = layer.toGeoJSON().geometry;

        onShapeDrawn(geojson);
      };

      const handleDrawDeleted = (e: any) => {
        // Handle deletion of zones through the map delete tool
        if (onZoneDelete) {
          e.layers.eachLayer((layer: L.Layer) => {
            // Check if this layer has a zoneId property (set when we add zone polygons)
            const options = (layer as L.Marker | L.Polygon).options as L.PathOptions & {
              zoneId?: string;
            };
            if (options && options.zoneId) {
              onZoneDelete(options.zoneId);
            }
          });
        }
      };

      map.on(L.Draw.Event.CREATED, handleDrawCreated);
      map.on(L.Draw.Event.DELETED, handleDrawDeleted);

      return () => {
        map.off(L.Draw.Event.CREATED, handleDrawCreated);
        map.off(L.Draw.Event.DELETED, handleDrawDeleted);
        if (drawControlRef.current) {
          map.removeControl(drawControlRef.current);
        }
        map.removeLayer(drawnItemsRef.current);
        map.removeLayer(editableZonesRef.current);
      };
    }, [map, onShapeDrawn, onZoneDelete]);

    // Clear drawn shapes when clearDrawnShapes prop changes
    useEffect(() => {
      if (clearDrawnShapes) {
        drawnItemsRef.current.clearLayers();
      }
    }, [clearDrawnShapes]);

    // Add dealer markers with stable server-side data + client-side clustering
    useEffect(() => {
      // Remove existing cluster group
      if (markerClusterGroupRef.current) {
        map.removeLayer(markerClusterGroupRef.current);
      }

      // Early return if bubbles should be hidden
      if (!showBubbles) {
        return;
      }

      // Create new marker cluster group with stable aggregation
      markerClusterGroupRef.current = (L as any).markerClusterGroup({
        maxClusterRadius: 60,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        // Custom cluster icon with stable totals
        iconCreateFunction: (cluster: any) => {
          // Calculate total recalls in cluster - now from stable server data!
          let totalRecalls = 0;
          cluster.getAllChildMarkers().forEach((marker: any) => {
            if (marker.options.recallCount) {
              totalRecalls += marker.options.recallCount;
            }
          });

          return new L.DivIcon({
            html: `<div style="background-color: #7C5F2C; color: white; 
            border-radius: 50%; width: 40px; height: 40px; 
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; border: 2px solid white; 
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            font-size: 14px; opacity: 0.9;">
            ${totalRecalls}
            </div>`,
            className: 'custom-cluster-icon',
            iconSize: new L.Point(40, 40)
          });
        }
      });

      // Create markers from stable server data
      const markers = dealerLocations.map(dealer => {
        const marker = L.marker([dealer.coordinates.latitude, dealer.coordinates.longitude], {
          icon: createDealerIcon(dealer.recallCount),
          recallCount: dealer.recallCount // Store for cluster calculations
        } as any);

        // Restored original popup content
        marker.bindPopup(`
        <div style="padding: 12px; max-width: 300px;">
          <h3 style="font-weight: 600; font-size: 16px; margin-bottom: 8px;">${dealer.dealerName}</h3>
          ${
            dealer.address && dealer.address !== 'N/A'
              ? `<p style="font-size: 14px; color: #666; margin-bottom: 12px;">${dealer.address}</p>`
              : ''
          }
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <span style="padding: 4px 8px; background: #fee2e2; color: #991b1b; border-radius: 4px; font-size: 12px; font-weight: 500;">
              ${dealer.recallCount} recalls
            </span>
            <span style="padding: 4px 8px; background: #dbeafe; color: #1e40af; border-radius: 4px; font-size: 12px; font-weight: 500;">
              ${dealer.vehicleCount} vehicles
            </span>
          </div>
        </div>
      `);

        return marker;
      });

      // Add all markers to cluster group
      markerClusterGroupRef.current.addLayers(markers);
      map.addLayer(markerClusterGroupRef.current);

      return () => {
        if (markerClusterGroupRef.current) {
          map.removeLayer(markerClusterGroupRef.current);
        }
      };
    }, [map, dealerLocations, showBubbles]);

    // Optimized zone rendering with memoization
    const zoneGeoJsonData = useMemo(() => {
      return zones
        .filter(zone => zone.metadata && zone.metadata['geometry'])
        .map((zone, index) => {
          const geometry = zone.metadata!['geometry'];
          // Same number the zone list shows. Previously this read metadata.recallCount — a
          // value frozen at draw time and scoped to whichever tenant the admin had selected —
          // and fell back to zip_code_count, which silently made the size label a function of
          // how many ZIPs a zone had. total_recalls_current is refreshed server-side under
          // the as-of cutoff rule.
          const recallCount = zone.total_recalls_current ?? 0;
          const zipCodeCount = zone.zip_code_count || 0;
          const zipCodes = zone.zip_codes || [];
          const color = uniformColor ? '#d4854a' : getZoneColor(recallCount);
          const isSelected = selectedZoneId === zone.id;

          return {
            id: zone.id,
            feature: {
              type: 'Feature',
              geometry: geometry as GeoJSON.Geometry,
              properties: {
                zoneId: zone.id,
                zoneName: zone.zone_name,
                recallCount,
                zipCodeCount,
                zipCodes,
                color,
                isSelected,
                zoneIndex: index,
                borderColor: uniformColor
                  ? '#b5693a'
                  : getZoneBorderColor(isSelected, recallCount, index)
              }
            } as GeoJSON.Feature
          };
        });
    }, [zones, selectedZoneId, uniformColor]);

    // Render zones
    useEffect(() => {
      // Clear existing zones from both editable group and direct map
      editableZonesRef.current.clearLayers();
      zoneLayersRef.current.forEach(layer => map.removeLayer(layer));
      zoneLayersRef.current.clear();

      // Add zones as polygons to the editable feature group
      zoneGeoJsonData.forEach(({ id, feature }) => {
        const geoJsonLayer = L.geoJSON(feature, {
          // Add zoneId to layer options for deletion tracking
          zoneId: feature.properties!['zoneId'],
          style: () => {
            const props = feature.properties!;
            const isSelected = props['isSelected'];
            const recallCount = props['recallCount'] || 0;
            const zoneIndex = props['zoneIndex'] || 0;

            // Clean, simplified styling focused on outer boundary
            return {
              // Fill styling
              fillColor: props['color'],
              fillOpacity: isSelected ? 0.4 : 0.2, // Clean, consistent opacity

              // Clean border styling - matches zone color for unified look
              color: props['borderColor'], // Matches zone color or black when selected
              weight: isSelected ? 4 : 3, // Clean, consistent border weight
              opacity: isSelected ? 1 : 0.8, // Full opacity for selected, slightly reduced for others

              // Restore dash patterns for better zone distinction, especially for extra large zones
              dashArray: getZoneDashPattern(recallCount, zoneIndex),

              // Clean visual effects
              className: 'zone-boundary',
              lineCap: 'round',
              lineJoin: 'round'
            };
          },
          onEachFeature: (feature: any, layer: any) => {
            const props = feature.properties;
            const zoneId = props!['zoneId'];
            const recallCount = props!['recallCount'] || 0;
            const isExtraLarge = recallCount >= 700;

            // Store zoneId on the layer for deletion handling
            (layer as any).options = { ...((layer as any).options || {}), zoneId };

            const zipCodes = props!['zipCodes'] || [];
            const sizeLabel = getShortSizeLabel(recallCount);
            const sizeLabelColor = getSizeLabelColor(recallCount);
            const fullSizeLabel = getZoneSizeLabel(recallCount);

            // Create modern ZIP codes display with remove buttons
            const zipCodesDisplay =
              zipCodes.length > 0
                ? `<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid hsl(var(--border));">
                <div style="
                  margin-bottom: 12px;
                ">
                  <span style="
                    font-size: 12px; 
                    font-weight: 600; 
                    color: hsl(var(--foreground));
                    display: block;
                    margin-bottom: 4px;
                  ">ZIP Codes (${zipCodes.length})</span>
                </div>
                <div style="
                  max-height: 120px; 
                  overflow-y: auto; 
                  display: flex; 
                  flex-wrap: wrap; 
                  gap: 6px;
                  padding-top: 8px;
                ">
                  ${zipCodes
                    .map((zip: string) => {
                      const canRemove = onRemoveZipCode && zipCodes.length > 1;
                      return `<span style="
                      background: hsl(var(--muted)); 
                      border: 1px solid hsl(var(--border));
                      padding: 4px 8px; 
                      border-radius: 6px; 
                      font-family: ui-monospace, 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
                      font-size: 11px;
                      font-weight: 500;
                      color: hsl(var(--foreground));
                      display: inline-block;
                      position: relative;
                      ${canRemove ? 'padding-right: 24px; margin-right: 2px;' : ''}
                      transition: all 0.15s ease;
                    " ${canRemove ? "onmouseover=\"this.style.background='hsl(var(--muted) / 0.8)'; this.style.borderColor='hsl(var(--border))'\"" : ''} 
                       ${canRemove ? "onmouseout=\"this.style.background='hsl(var(--muted))'; this.style.borderColor='hsl(var(--border))'\"" : ''}>
                      ${zip}
                      ${
                        canRemove
                          ? `<button 
                        onclick="window.removeZipFromZone('${zoneId}', '${zip}')"
                        style="
                          position: absolute;
                          top: -6px;
                          right: -6px;
                          width: 18px;
                          height: 18px;
                          border-radius: 50%;
                          background: hsl(var(--destructive));
                          color: hsl(var(--destructive-foreground));
                          border: 2px solid hsl(var(--background));
                          font-size: 11px;
                          line-height: 1;
                          cursor: pointer;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                          transition: all 0.15s ease;
                          z-index: 10;
                        "
                        onmouseover="this.style.transform='scale(1.1)'"
                        onmouseout="this.style.transform='scale(1)'"
                        title="Remove ${zip}"
                      >×</button>`
                          : ''
                      }
                    </span>`;
                    })
                    .join('')}
                </div>
              </div>`
                : '';

            // Modern popup design with application theme colors
            const popupContent = `
            <div style="
              font-family: system-ui, -apple-system, 'Segoe UI', 'Roboto', sans-serif;
              background: hsl(var(--background));
              border-radius: 12px;
              padding: 0;
              margin: -4px;
              min-width: 280px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
              border: 1px solid hsl(var(--border));
            ">
              <!-- Header -->
              <div style="
                background: hsl(var(--primary));
                padding: 16px;
                border-radius: 12px 12px 0 0;
                color: hsl(var(--primary-foreground));
                position: relative;
                overflow: hidden;
              ">
                <div style="
                  position: absolute;
                  top: -50%;
                  right: -20px;
                  width: 100px;
                  height: 100px;
                  background: rgba(255, 255, 255, 0.1);
                  border-radius: 50%;
                "></div>
                <div style="
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  position: relative;
                ">
                  <div>
                    <h3 style="
                      margin: 0;
                      font-size: 16px;
                      font-weight: 600;
                      line-height: 1.3;
                      color: hsl(var(--primary-foreground));
                    ">${props!['zoneName']}</h3>
                    <p style="
                      margin: 4px 0 0 0;
                      font-size: 12px;
                      opacity: 0.9;
                      color: hsl(var(--primary-foreground) / 0.8);
                    ">${fullSizeLabel} Zone</p>
                  </div>
                  <div style="
                    background: ${sizeLabelColor};
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 14px;
                    color: ${isExtraLarge ? 'hsl(var(--destructive-foreground))' : 'hsl(var(--primary-foreground))'};
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                  ">${sizeLabel}</div>
                </div>
              </div>

              <!-- Content -->
              <div style="padding: 16px;">
                ${
                  isExtraLarge
                    ? `
                  <div style="
                    background: hsl(var(--destructive) / 0.1);
                    border: 1px solid hsl(var(--destructive) / 0.2);
                    border-radius: 8px;
                    padding: 12px;
                    margin-bottom: 12px;
                  ">
                    <div style="
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin-bottom: 4px;
                    ">
                      <span style="font-size: 16px;">⚠️</span>
                      <span style="
                        font-weight: 600;
                        color: hsl(var(--destructive));
                        font-size: 12px;
                      ">Zone Too Large</span>
                    </div>
                    <p style="
                      margin: 0;
                      font-size: 11px;
                      color: hsl(var(--destructive) / 0.8);
                      line-height: 1.4;
                    ">Consider breaking into smaller zones for better management.</p>
                  </div>
                `
                    : ''
                }

                <!-- Stats Grid -->
                <div style="
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 12px;
                  margin-bottom: ${zipCodesDisplay ? '0' : '8px'};
                ">
                  <div style="
                    background: hsl(var(--muted) / 0.3);
                    border: 1px solid hsl(var(--border));
                    border-radius: 8px;
                    padding: 10px;
                    text-align: center;
                  ">
                    <div style="
                      font-size: 18px;
                      font-weight: 700;
                      color: ${isExtraLarge ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'};
                      margin-bottom: 2px;
                    ">${recallCount.toLocaleString()}</div>
                    <div style="
                      font-size: 10px;
                      color: hsl(var(--muted-foreground));
                      text-transform: uppercase;
                      font-weight: 600;
                      letter-spacing: 0.05em;
                    ">Recalls</div>
                  </div>
                  <div style="
                    background: hsl(var(--muted) / 0.3);
                    border: 1px solid hsl(var(--border));
                    border-radius: 8px;
                    padding: 10px;
                    text-align: center;
                  ">
                    <div style="
                      font-size: 18px;
                      font-weight: 700;
                      color: hsl(var(--primary));
                      margin-bottom: 2px;
                    ">${props!['zipCodeCount'] || 0}</div>
                    <div style="
                      font-size: 10px;
                      color: hsl(var(--muted-foreground));
                      text-transform: uppercase;
                      font-weight: 600;
                      letter-spacing: 0.05em;
                    ">ZIP Codes</div>
                  </div>
                </div>

                ${zipCodesDisplay}
              </div>
            </div>
          `;

            layer.bindPopup(popupContent, {
              maxWidth: 320,
              className: 'modern-zone-popup'
            });
          }
        } as any);

        // Add to editable feature group so it can be deleted with the map delete tool
        editableZonesRef.current.addLayer(geoJsonLayer);
        zoneLayersRef.current.set(id, geoJsonLayer);

        // Add warning marker for extra large zones
        const isExtraLarge = feature.properties!['recallCount'] >= 700;
        if (isExtraLarge) {
          // Calculate center of the zone for warning marker placement
          const bounds = geoJsonLayer.getBounds();
          const center = bounds.getCenter();

          const warningMarker = L.marker([center.lat, center.lng], {
            icon: L.divIcon({
              html: `<div style="
              background: linear-gradient(45deg, #dc2626, #ef4444);
              color: white;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 16px;
              border: 2px solid white;
              box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);
              animation: pulse 2s infinite;
            ">⚠️</div>
            <style>
              @keyframes pulse {
                0% { transform: scale(1); box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4); }
                50% { transform: scale(1.1); box-shadow: 0 4px 12px rgba(220, 38, 38, 0.6); }
                100% { transform: scale(1); box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4); }
              }
            </style>`,
              className: 'warning-marker',
              iconSize: L.point(30, 30)
            })
          });

          warningMarker.bindPopup(`
          <div style="padding: 8px; text-align: center;">
            <div style="font-weight: 600; color: #dc2626; margin-bottom: 4px;">⚠️ Zone Too Large</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              ${feature.properties!['zoneName']}<br>
              ${feature.properties!['recallCount']?.toLocaleString()} recalls
            </div>
            <div style="font-size: 11px; color: #dc2626; font-style: italic;">
              Break into smaller zones
            </div>
          </div>
        `);

          editableZonesRef.current.addLayer(warningMarker);
        }
      });

      return () => {
        editableZonesRef.current.clearLayers();
        zoneLayersRef.current.clear();
      };
    }, [map, zoneGeoJsonData]);

    // Set up global function for ZIP code removal
    useEffect(() => {
      if (onRemoveZipCode) {
        (window as any).removeZipFromZone = (zoneId: string, zipCode: string) => {
          const zone = zones.find(z => z.id === zoneId);
          if (zone) {
            onRemoveZipCode(zone, zipCode);
          }
        };
      }

      return () => {
        delete (window as any).removeZipFromZone;
      };
    }, [zones, onRemoveZipCode]);

    // Optimized bounds change handler
    const handleMapMove = useCallback(() => {
      if (!onMapBoundsChange) return;

      if (boundsTimerRef.current) {
        clearTimeout(boundsTimerRef.current);
      }

      boundsTimerRef.current = setTimeout(() => {
        try {
          const bounds = map.getBounds();
          onMapBoundsChange({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          });
        } catch (error) {
          // Silently handle errors
        }
      }, 500); // Increased debounce for better performance
    }, [map, onMapBoundsChange]);

    // Handle map bounds changes for viewport loading
    useEffect(() => {
      if (!onMapBoundsChange) return;

      // Set initial bounds once after map is ready (use a small delay to ensure map is fully initialized)
      const initialTimer = setTimeout(() => {
        handleMapMove();
      }, 100);

      map.on('moveend', handleMapMove);
      map.on('zoomend', handleMapMove);

      return () => {
        clearTimeout(initialTimer);
        if (boundsTimerRef.current) {
          clearTimeout(boundsTimerRef.current);
        }
        map.off('moveend', handleMapMove);
        map.off('zoomend', handleMapMove);
      };
    }, [map, handleMapMove]);

    return null;
  }
);

MapController.displayName = 'MapController';

export const ZoneDrawingMapOptimized: React.FC<ZoneDrawingMapOptimizedProps> = React.memo(
  ({
    dealerLocations,
    zones,
    onShapeDrawn,
    onMapBoundsChange,
    selectedZoneId,
    onZoneDelete,
    onRemoveZipCode,
    loading = false,
    clearDrawnShapes,
    showBubbles = true,
    uniformColor = false
  }) => {
    // Center map on Florida (same as recall map)
    const center: [number, number] = [27.9944024, -81.7602544];
    const zoom = 7;

    return (
      <div className='relative w-full h-full' style={{ overflow: 'hidden' }}>
        {loading && (
          <div className='absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-md p-2'>
            <div className='flex items-center gap-2'>
              <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
              <span className='text-sm text-muted-foreground'>Loading dealers...</span>
            </div>
          </div>
        )}
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%', position: 'relative' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <MapController
            onShapeDrawn={onShapeDrawn}
            dealerLocations={dealerLocations}
            zones={zones}
            showBubbles={showBubbles}
            uniformColor={uniformColor}
            {...(selectedZoneId && { selectedZoneId })}
            {...(onMapBoundsChange && { onMapBoundsChange })}
            {...(onZoneDelete && { onZoneDelete })}
            {...(onRemoveZipCode && { onRemoveZipCode })}
            {...(clearDrawnShapes && { clearDrawnShapes })}
          />
        </MapContainer>
      </div>
    );
  }
);

ZoneDrawingMapOptimized.displayName = 'ZoneDrawingMapOptimized';
